import { QueryClient } from '@tanstack/react-query';

/**
 * Optimized query client configuration for better performance
 */
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30000, // 30 seconds - reduce API calls
        gcTime: 300000, // 5 minutes - keep data in memory longer
        refetchOnWindowFocus: false, // Reduce unnecessary refetches
        refetchOnReconnect: 'always', // Only refetch on actual reconnection
        retry: (failureCount, error: any) => {
          // Don't retry auth errors or network errors
          if (error?.code === 'PGRST301' || error?.name === 'NetworkError') {
            return false;
          }
          return failureCount < 2; // Reduced retry attempts
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
      },
      mutations: {
        retry: 1, // Reduced retry for mutations
        retryDelay: 1000
      }
    }
  });
};

/**
 * Query patterns that are safe to cache longer
 */
export const longCacheQueries = [
  'template-timeline',
  'zodiac-signs',
  'subscription-plans'
];

/**
 * Query patterns that should have shorter cache times
 */
export const shortCacheQueries = [
  'notifications',
  'friends',
  'timeline-entries'
];

/**
 * Batch query invalidation helper
 */
export const batchInvalidateQueries = (
  queryClient: QueryClient, 
  patterns: string[], 
  userId?: string
) => {
  const promise = Promise.all(
    patterns.map(pattern => 
      queryClient.invalidateQueries({ 
        queryKey: userId ? [pattern, userId] : [pattern],
        exact: false 
      })
    )
  );
  return promise;
};

/**
 * Performance monitoring for queries
 */
export const logSlowQueries = (queryClient: QueryClient) => {
  const cache = queryClient.getQueryCache();
  
  cache.subscribe((event) => {
    if (event?.type === 'updated' && event.query.state.data) {
      const duration = Date.now() - (event.query.state.dataUpdatedAt || 0);
      if (duration > 2000) { // Log queries taking more than 2 seconds
        console.warn(`Slow query detected: ${event.query.queryKey.join('-')} took ${duration}ms`);
      }
    }
  });
};